import { createAsyncStoragePersister } from "@tanstack/query-async-storage-persister";
import { QueryClient } from "@tanstack/react-query";
import {
  type PersistQueryClientOptions,
  persistQueryClientRestore,
  persistQueryClientSubscribe,
} from "@tanstack/react-query-persist-client";
import { del, get, set } from "idb-keyval";

import { query } from "@/queries";

declare const __BUILD_ID__: string;

/** How long a cache saved to disk is worth restoring. */
const PERSIST_MAX_AGE_MS = 24 * 60 * 60 * 1000;

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      // Going back to a screen read in the last 30 s does not ask again.
      // Writes do not wait it out: each one invalidates its resource's base.
      // Past it, returning to the tab asks again (refetchOnWindowFocus is on
      // by default), so another user's or another tab's write shows up.
      staleTime: 30_000,
      // Restored data must outlive its time on disk, or gc drops it first.
      gcTime: PERSIST_MAX_AGE_MS,
    },
  },
});

const persister = createAsyncStoragePersister({
  storage: { getItem: get, setItem: set, removeItem: del },
});

const persistOptions: PersistQueryClientOptions = {
  queryClient,
  persister,
  // A new build may change a response's shape: drop what an older one saved.
  buster: __BUILD_ID__,
  maxAge: PERSIST_MAX_AGE_MS,
  dehydrateOptions: {
    // The session is the guard's to check against the server on every load,
    // so it is not read back from disk.
    shouldDehydrateQuery: ({ queryKey, state }) =>
      state.status === "success" && queryKey[0] !== query.user.base[0],
  },
};

/**
 * Loads the cache saved on disk, then keeps saving it. Awaited before the
 * router mounts: loaders that ran earlier would fetch what is about to land.
 */
export async function restoreQueryCache() {
  // No IndexedDB (private mode) or a corrupt save: start empty, as before.
  await persistQueryClientRestore(persistOptions).catch(() => undefined);
  persistQueryClientSubscribe(persistOptions);
}

/** Deletes the cache saved on this device. */
export async function forgetSavedQueryCache() {
  // No IndexedDB (private mode): nothing was saved.
  await Promise.resolve(persister.removeClient()).catch(() => undefined);
}
